import { useState, useMemo, useCallback } from 'react';
import { CardSummary } from '../../types/cards';
import { Card } from './Card';
import { CardDetailModal } from './CardDetailModal';
import './CardInventoryGrid.css';

interface CardInventoryGridProps {
  cards: CardSummary[];
  isLoading?: boolean;
}

type SortOption = 'grade-desc' | 'grade-asc';
export function CardInventoryGrid({
  cards,
  isLoading,
}: CardInventoryGridProps) {
  const [selectedCard, setSelectedCard] = useState<CardSummary | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sortBy, setSortBy] = useState<SortOption>('grade-desc');
  const [activeCardId, setActiveCardId] = useState<number | null>(null);

  const sortedCards = useMemo(() => {
    const sorted = [...cards];
    const gradeOrder: Record<string, number> = { 
      SS: 6, S: 5, A: 4, B: 3, C: 2, D: 1 
    };
    
    switch (sortBy) {
      case 'grade-desc': // 내림차순: SS → S → A → B → C → D
        return sorted.sort((a, b) => 
          (gradeOrder[b.grade] || 0) - (gradeOrder[a.grade] || 0)
        );
      case 'grade-asc': // 오름차순: D → C → B → A → S → SS
        return sorted.sort((a, b) => 
          (gradeOrder[a.grade] || 0) - (gradeOrder[b.grade] || 0)
        );
      default:
        return sorted;
    }
  }, [cards, sortBy]);

  const handleShowDetails = useCallback((card: CardSummary) => {
    setSelectedCard(card);
    setIsModalOpen(true);
  }, []);

  const handleToggleCard = useCallback((cardId: number) => {
    setActiveCardId((prev) => (prev === cardId ? null : cardId));
  }, []);

  const handleUpgrade = useCallback((card: CardSummary) => {
    console.info('업그레이드 준비 중', card);
  }, []);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedCard(null);
  };

  return (
    <section className="card-inventory">
      {/* 정렬 드롭다운 */}
      <div className="card-inventory__controls">
        <select
          className="card-inventory__sort"
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value as SortOption)}
        >
          <option value="grade-desc">등급 높은 순</option>
          <option value="grade-asc">등급 낮은 순</option>
        </select>
      </div>

      {isLoading ? (
        <div className="card-inventory__loading">카드를 불러오는 중...</div>
      ) : sortedCards.length === 0 ? (
        <div className="card-inventory__empty">
          아직 보유 중인 카드가 없습니다. 카드 소환을 해보세요!
        </div>
      ) : (
        <div className="card-inventory__grid-wrapper">
          <div className="card-inventory__grid">
            {sortedCards.map((card) => {
              const isActive = activeCardId === card.id;
              return (
                <div
                  key={card.id}
                  className={`card-grid-item ${isActive ? 'card-grid-item--active' : ''}`}
                >
                  <div
                    className="card-grid-item__body"
                    role="button"
                    tabIndex={0}
                    onClick={() => handleToggleCard(card.id)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleToggleCard(card.id);
                      }
                    }}
                  >
                    <Card card={card} />
                  </div>
                  <div className={`card-grid-item__overlay ${isActive ? 'is-visible' : ''}`}>
                    <div className="card-grid-item__actions">
                      <button
                        type="button"
                        className="card-grid-item__action"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleShowDetails(card);
                        }}
                      >
                        상세보기
                      </button>
                      <button
                        type="button"
                        className="card-grid-item__action card-grid-item__action--secondary"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleUpgrade(card);
                        }}
                        disabled
                        title="업그레이드 기능 준비 중입니다."
                      >
                        업그레이드
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <CardDetailModal
        card={selectedCard}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </section>
  );
}


